import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { humanizeCondition, deriveExemptionPlace } from "../utils/humanizeCondition";

const EMPTY_FORM = {
  id: null,
  name: "",
  type: "",
  logic_operator: "ALL",
  params: {},
};

export default function ConditionModal({
  show,
  initialValue,
  rateConcepts = [],
  places = [],
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(EMPTY_FORM);

  // ✅ Reset / Load cuando se abre el modal o cambia lo que estás editando
  useEffect(() => {
    if (!show) return;

    // NUEVA
    if (!initialValue) {
      setForm({
        id: null,
        name: "",
        type: "",
        logic_operator: "ALL",
        params: {},
      });
      return;
    }

    // EDICIÓN: clonar + normalizar EXEMPTION
    const next = {
      ...initialValue,
      params: { ...(initialValue.params ?? {}) },
    };

    if (next.type === "EXEMPTION") {
      const derived = deriveExemptionPlace(next.params);

      next.params.place_type = next.params.place_type || derived.place_type;
      next.params.place_value = next.params.place_value || derived.place_value;
    }

    setForm(next);
  }, [show, initialValue]);

  const { valid, errors } = useMemo(() => validateCondition(form), [form]);

  const humanSummary = useMemo(() => {
    if (!form.type) return null;
    return humanizeCondition(form, { places });
  }, [form, places]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {initialValue ? "Editar condición" : "Nueva condición"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Nombre */}
        <Form.Group className="mb-3">
          <Form.Label>Nombre de la condición</Form.Label>
          <Form.Control
            type="text"
            disabled={!!initialValue} // si quieres permitir editar nombre, quita esto
            isInvalid={!!errors.name}
            placeholder="Ej: Recargo nocturno"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Form.Control.Feedback type="invalid">
            {errors.name}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Tipo */}
        <Form.Group className="mb-3">
          <Form.Label>Tipo de condición</Form.Label>
          <Form.Select
            value={form.type}
            isInvalid={!!errors.type}
            onChange={(e) =>
              setForm({
                ...form,
                type: e.target.value,
                params: {}, // reset params al cambiar tipo
              })
            }
          >
            <option value="">Seleccione tipo</option>
            <option value="TIME_RANGE">Horario</option>
            <option value="DAY_KIND">Días específicos</option>
            <option value="EXEMPTION">Excepción</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {errors.type}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Sub-form dinámico */}
        <DynamicConditionForm
          form={form}
          setForm={setForm}
          places={places}
          rateConcepts={rateConcepts}
          errors={errors}
        />

        {/* Preview */}
        {humanSummary && (
          <div className="mt-4 p-3 rounded bg-light border">
            <strong>📌 Vista previa de la condición</strong>
            <p className="mb-0 mt-2 text-muted">{humanSummary}</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" disabled={!valid} onClick={() => onSave(form)}>
          Guardar condición
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function validateCondition(form) {
  const errors = {};

  if (!form.name?.trim()) errors.name = "El nombre es obligatorio.";
  if (!form.type) errors.type = "Debe seleccionar un tipo de condición.";

  switch (form.type) {
    case "TIME_RANGE":
      if (!form.params?.start) errors.start = "Debe seleccionar la hora de inicio.";
      if (!form.params?.end) errors.end = "Debe seleccionar la hora de fin.";
      break;

    case "DAY_KIND": {
      const days = form.params?.days || [];
      // ✅ soporta ambos nombres
      const holidays = Boolean(form.params?.holidays ?? false);

      if (!days.length && !holidays) {
        errors.days = "Debe seleccionar al menos un día o incluir festivos.";
      }

      break;
    }

    case "EXEMPTION":
      if (!form.params?.place_type) errors.place_type = "Debe seleccionar el tipo de lugar.";
      if (!form.params?.place_value) errors.place_value = "Debe seleccionar un lugar.";
      if (!form.params?.exempt_concept) errors.exempt_concept = "Debe seleccionar el concepto a excluir.";
      break;

    default:
      break;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

const WEEK_DAYS = [
  { label: "Domingo", value: 0 },
  { label: "Lunes", value: 1 },
  { label: "Martes", value: 2 },
  { label: "Miércoles", value: 3 },
  { label: "Jueves", value: 4 },
  { label: "Viernes", value: 5 },
  { label: "Sábado", value: 6 },
];

function DynamicConditionForm({
  form,
  setForm,
  places = [],
  rateConcepts = [],
  errors = {},
}) {
  switch (form.type) {
    /* ===============================
       ⏰ HORARIO (controlado)
    =============================== */
    case "TIME_RANGE": {
      const start = form.params?.start ?? "";
      const end = form.params?.end ?? "";
      const crosses = Boolean(form.params?.crosses_midnight ?? false);

      return (
        <>
          <Form.Group className="mb-2">
            <Form.Label>Hora inicio</Form.Label>
            <Form.Control
              type="time"
              value={start}
              isInvalid={!!errors.start}
              onChange={(e) =>
                setForm({
                  ...form,
                  params: { ...form.params, start: e.target.value },
                })
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.start}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Hora fin</Form.Label>
            <Form.Control
              type="time"
              value={end}
              isInvalid={!!errors.end}
              onChange={(e) =>
                setForm({
                  ...form,
                  params: { ...form.params, end: e.target.value },
                })
              }
            />
            <Form.Control.Feedback type="invalid">
              {errors.end}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Check
            type="checkbox"
            label="Cruza medianoche"
            checked={crosses}
            onChange={(e) =>
              setForm({
                ...form,
                params: { ...form.params, crosses_midnight: e.target.checked },
              })
            }
          />
        </>
      );
    }

    /* ===============================
       📅 DÍAS ESPECÍFICOS (controlado)
    =============================== */
    case "DAY_KIND": {
      const selectedDays = form.params?.days ?? [];
      // ✅ lee ambos, escribe uno solo
      const holidays = Boolean(form.params?.holidays ?? false);
      
      const toggleDay = (dayValue) => {
        const exists = selectedDays.includes(dayValue);
        const newDays = exists
          ? selectedDays.filter((d) => d !== dayValue)
          : [...selectedDays, dayValue];

        setForm({
          ...form,
          params: { ...form.params, days: newDays.sort() },
        });
      };

      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Días de la semana</Form.Label>

            <div className="d-flex flex-wrap gap-2">
              {WEEK_DAYS.map((day) => (
                <Form.Check
                  key={day.value}
                  type="checkbox"
                  label={day.label}
                  checked={selectedDays.includes(day.value)}
                  onChange={() => toggleDay(day.value)}
                />
              ))}
            </div>

            {errors.days && (
              <div className="text-danger small mt-1">{errors.days}</div>
            )}
          </Form.Group>

          <Form.Check
            type="checkbox"
            label="Incluir festivos"
            checked={holidays}
            onChange={(e) =>
              setForm({
                ...form,
                params: {
                  ...form.params,
                  holidays: e.target.checked,
                },
              })
            }
          />
        </>
      );
    }

    /* ===============================
       🚫 EXCEPCIÓN (controlado)
    =============================== */
    case "EXEMPTION": {
      const place_type = form.params?.place_type ?? "";
      const place_value = form.params?.place_value ?? "";
      const exempt_concept = form.params?.exempt_concept ?? "";

      const filteredPlaces = place_type
        ? places.filter((p) => p.kind === place_type)
        : [];

      return (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Tipo de lugar</Form.Label>
            <Form.Select
              value={place_type}
              isInvalid={!!errors.place_type}
              onChange={(e) =>
                setForm({
                  ...form,
                  params: {
                    ...form.params,
                    place_type: e.target.value,
                    place_value: "", // reset
                    exempt_concept: form.params?.exempt_concept ?? "",
                  },
                })
              }
            >
              <option value="">Seleccione</option>
              <option value="ORIGIN">Origen</option>
              <option value="DESTINATION">Destino</option>
              <option value="POINT">Origen/Destino</option>
              <option value="ZONE">Zona</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.place_type}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Lugar (ahora sí aparece y carga al editar) */}
          {place_type && (
            <Form.Group className="mb-3">
              <Form.Label>Lugar</Form.Label>
              <Form.Select
                value={place_value}
                isInvalid={!!errors.place_value}
                onChange={(e) =>
                  setForm({
                    ...form,
                    params: { ...form.params, place_value: e.target.value },
                  })
                }
              >
                <option value="">Seleccione</option>
                {filteredPlaces.map((p) => (
                  <option key={p.id} value={p.code || p.name}>
                    {p.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.place_value}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Concepto a excluir</Form.Label>
            <Form.Select
              value={exempt_concept}
              isInvalid={!!errors.exempt_concept}
              onChange={(e) =>
                setForm({
                  ...form,
                  params: { ...form.params, exempt_concept: e.target.value },
                })
              }
            >
              <option value="">Seleccione</option>
              {rateConcepts.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.exempt_concept}
            </Form.Control.Feedback>
          </Form.Group>
        </>
      );
    }

    default:
      return null;
  }
}
